import React from "react";
import { Check } from "Components/DesignSystem/Iconography/Icons";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import Pagination from "Components/DesignSystem/Pagination/Pagination";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import { TAttributeChoice } from "types/Pages/ProductDelta";
import { TDBMDirection, TDBMTick } from "../RuleComponents/dataTypes";

type TDBMMatrixTableProps = {
  sourceOptions: TAttributeChoice[];
  defaultsOptions: TAttributeChoice[];
  ticks: TDBMTick[];
  onToggleTick: (sourceValue: string, defaultsValue: string) => void;
  sourceSelectType: "Single-select" | "Multi-select";
  defaultsSelectType: "Single-select" | "Multi-select";
  direction: TDBMDirection;
  sourceLabel: string;
  defaultsLabel: string;
  page: number;
  pageSize: number;
};

// Bare numbers behind a visible "Show" label — the ISR page-size pattern
// (`RulesTable.tsx`), not the DS Pagination's built-in "20 per page" control.
const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500].map((n) => ({ value: n, label: String(n) }));

// Fixed column widths. The Defaults (left, sticky) column matches the direction
// band's Bidirectional/Forward cell (`tw-w-40`) so the two line up; Source
// columns are a fixed width so a long option name truncates instead of
// widening its column. Widths sit on an inner block — a table cell's own
// width is only a minimum and grows to fit its content.
const DEFAULTS_COL_WIDTH = "tw-w-40";
const SOURCE_COL_WIDTH = "tw-w-32";

const LOCKED_TOOLTIP = "Locked: this field is single-select and already has a default";
const BREACH_TOOLTIP = "Breaches the single-select constraint — uncheck to resolve";

/**
 * The DBM matrix — same look/interaction grammar as CBM's `MatrixTable.tsx`
 * (bordered grid, sticky header/left-column) but the checked cell means "this
 * Source option defaults this Defaults option" instead of a compatibility
 * rating, and cells can LOCK (single-select axis already has a default) or
 * BREACH (more than one default on a single-select axis, e.g. from a bad Bulk
 * Action upload) — states CBM's grid has no equivalent of.
 *
 * Visual axes only: Defaults options render as ROWS (left) and Source options
 * as COLUMNS (top) — a UI-only transpose. The tick data model, the
 * `onToggleTick(sourceValue, defaultsValue)` call order and every other prop
 * are unchanged; only which array renders on which axis moved.
 */
const DBMMatrixTable = ({
  sourceOptions,
  defaultsOptions,
  ticks,
  onToggleTick,
  sourceSelectType,
  defaultsSelectType,
  direction,
  sourceLabel,
  defaultsLabel,
  page,
  pageSize,
}: TDBMMatrixTableProps) => {
  const isChecked = (sourceValue: string, defaultsValue: string) =>
    ticks.some((t) => t.sourceValue === sourceValue && t.defaultsValue === defaultsValue);

  // Each select type constrains the OTHER axis: a single-select Defaults field
  // holds one value, so each Source option may default at most one Defaults
  // option (one tick per Source column); a single-select Source field means each
  // Defaults option may be defaulted by at most one Source option (one per row).
  const sourceHasDefault = (sourceValue: string) =>
    defaultsSelectType === "Single-select" && ticks.some((t) => t.sourceValue === sourceValue);
  const defaultsHasDefault = (defaultsValue: string) =>
    sourceSelectType === "Single-select" && ticks.some((t) => t.defaultsValue === defaultsValue);

  const sourceBreaches = new Set<string>();
  const defaultsBreaches = new Set<string>();
  if (defaultsSelectType === "Single-select") {
    const counts = new Map<string, number>();
    ticks.forEach((t) => counts.set(t.sourceValue, (counts.get(t.sourceValue) ?? 0) + 1));
    counts.forEach((count, key) => {
      if (count > 1) sourceBreaches.add(key);
    });
  }
  if (sourceSelectType === "Single-select") {
    const counts = new Map<string, number>();
    ticks.forEach((t) => counts.set(t.defaultsValue, (counts.get(t.defaultsValue) ?? 0) + 1));
    counts.forEach((count, key) => {
      if (count > 1) defaultsBreaches.add(key);
    });
  }

  const start = (page - 1) * pageSize;
  const pagedDefaultsOptions = defaultsOptions.slice(start, start + pageSize);

  const directionGlyph = direction === "bidirectional" ? "⇄" : "→";
  const directionLabel = direction === "bidirectional" ? "Bidirectional" : "Forward";

  return (
    <div data-testid="rules-dbmmatrixtable">
      {/* Fixed band — outside the scroll container, never moves with it. */}
      <div
        className="tw-flex tw-border tw-border-b-0 tw-border-edge tw-bg-surface-sunken tw-rounded-t-srf"
        data-testid="rules-dbmmatrixtable-direction-band"
      >
        {/* `box-content`: 160px of content PLUS the 1px divider, so the divider lands on the
            table's own corner/first-column divider below it (the table's outer borders are
            hidden, so its corner cell starts flush with this one). Border-box, the divider
            sat ~1.5px left of the column line (AB#15316). */}
        <div className={`${DEFAULTS_COL_WIDTH} tw-box-content tw-shrink-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-2 tw-border-r tw-border-edge`}>
          <span aria-hidden="true" className="tw-text-xl tw-text-ink tw-leading-none">{directionGlyph}</span>
          <span className="tw-text-xs tw-uppercase tw-text-ink-tertiary tw-tracking-wide">{directionLabel}</span>
        </div>
        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-2 tw-min-w-0">
          <span className="tw-text-sm tw-font-semibold tw-text-ink tw-truncate tw-max-w-full" title={sourceLabel} data-testid="rules-dbmmatrixtable-source-label">{sourceLabel}</span>
          <span className="tw-text-xs tw-uppercase tw-text-ink-tertiary tw-tracking-wide">source</span>
        </div>
      </div>

      {/* Scrollable matrix — its own bordered container; the page body never scrolls horizontally. */}
      <div
        className="tw-max-h-[420px] tw-overflow-auto tw-border tw-border-edge tw-rounded-b-srf"
        data-testid="rules-dbmmatrixtable-scroll"
      >
        {/* `border-hidden` on a collapsed table suppresses every cell's OUTER border: the
            scroll container draws the frame. Without it the edge cells' borders sat inside
            the container's, doubling the outer lines and pushing the corner/first-column
            divider off the direction band's (AB#15316). */}
        <table className="tw-border-collapse tw-border-hidden">
          <thead>
            <tr>
              <th className="tw-sticky tw-top-0 tw-left-0 tw-z-20 tw-bg-surface-sunken tw-border tw-border-edge tw-p-0 tw-text-left">
                <div className={`${DEFAULTS_COL_WIDTH} tw-box-border tw-p-2`}>
                  <div className="tw-text-sm tw-font-semibold tw-text-ink tw-truncate" title={defaultsLabel} data-testid="rules-dbmmatrixtable-defaults-label">{defaultsLabel}</div>
                  <div className="tw-text-xs tw-uppercase tw-text-ink-tertiary tw-tracking-wide">defaults</div>
                </div>
              </th>
              {sourceOptions.map((col) => {
                const value = col.av_value ?? col.value ?? "";
                const breached = sourceBreaches.has(value);
                return (
                  <th
                    key={col.av_id ?? col.id ?? value}
                    className={`tw-sticky tw-top-0 tw-z-10 tw-border tw-border-edge tw-p-0 tw-text-sm tw-font-medium ${
                      breached ? "tw-text-tone-danger-ink tw-bg-tone-danger-tint" : "tw-text-ink tw-bg-surface-sunken"
                    }`}
                    data-testid={`rules-dbmmatrixtable-col-header-${value}`}
                  >
                    {/* `text-center` explicitly: preflight resets `th` to `text-align: inherit`,
                        which left the label off-centre over its centred checkboxes (AB#15316). */}
                    <div className={`${SOURCE_COL_WIDTH} tw-box-border tw-p-2 tw-truncate tw-text-center`} title={value}>{value}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedDefaultsOptions.map((row) => {
              const rowValue = row.av_value ?? row.value ?? "";
              const rowBreached = defaultsBreaches.has(rowValue);
              return (
                <tr key={row.av_id ?? row.id ?? rowValue}>
                  <td
                    className={`tw-sticky tw-left-0 tw-z-10 tw-border tw-border-edge tw-p-0 tw-text-sm tw-font-medium ${
                      rowBreached ? "tw-text-tone-danger-ink tw-bg-tone-danger-tint" : "tw-text-ink tw-bg-surface-sunken"
                    }`}
                    data-testid={`rules-dbmmatrixtable-row-header-${rowValue}`}
                  >
                    <div className={`${DEFAULTS_COL_WIDTH} tw-box-border tw-p-2 tw-truncate`} title={rowValue}>{rowValue}</div>
                  </td>
                  {sourceOptions.map((col) => {
                    const colValue = col.av_value ?? col.value ?? "";
                    const checked = isChecked(colValue, rowValue);
                    const breached = rowBreached || sourceBreaches.has(colValue);
                    const locked = !checked && !breached && (defaultsHasDefault(rowValue) || sourceHasDefault(colValue));
                    const cellTestId = `rules-dbmmatrixtable-cell-${colValue}-${rowValue}`;
                    const cell = (
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        disabled={locked}
                        onClick={() => onToggleTick(colValue, rowValue)}
                        data-testid={cellTestId}
                        className={`tw-mx-auto tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-ctl tw-border motion-safe:tw-transition-colors motion-safe:tw-duration-fast ${
                          breached
                            ? "tw-border-tone-danger-ink tw-bg-tone-danger-tint tw-text-tone-danger-ink"
                            : checked
                              ? "tw-border-accent tw-bg-accent-tint tw-text-accent tw-cursor-pointer"
                              : locked
                                ? "tw-border-edge tw-bg-surface-sunken tw-opacity-50 tw-cursor-not-allowed"
                                : "tw-border-edge-strong tw-bg-surface hover:tw-border-accent tw-cursor-pointer"
                        }`}
                      >
                        {checked && <Check size={14} aria-hidden="true" />}
                      </button>
                    );
                    return (
                      <td key={col.av_id ?? col.id ?? colValue} className="tw-border tw-border-edge tw-p-2 tw-text-center">
                        {locked ? (
                          <Tooltip content={LOCKED_TOOLTIP} data-testid={`${cellTestId}-tooltip`}>{cell}</Tooltip>
                        ) : breached ? (
                          <Tooltip content={BREACH_TOOLTIP} data-testid={`${cellTestId}-tooltip`}>{cell}</Tooltip>
                        ) : cell}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

type TDBMMatrixPagerProps = {
  /** Defaults options count — the matrix pages its ROWS. */
  totalItems: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

/**
 * The matrix's "Show [20]" + page control. Its own component (2026-10-05, AB#15317)
 * so `DBMRule` can render it in the SidePanel's pinned `footer` — under a rule, at the
 * bottom of the panel however few rows the matrix has — instead of floating right
 * under the last row.
 */
export const DBMMatrixPager = ({ totalItems, page, pageSize, onPageChange, onPageSizeChange }: TDBMMatrixPagerProps) => (
  <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap" data-testid="rules-dbmmatrixtable-pager">
    <div className="tw-flex tw-items-center tw-gap-2">
      <span className="tw-text-sm tw-text-ink-secondary">Show</span>
      <Dropdown
        label=""
        aria-label="Records per page"
        size="sm"
        className="tw-w-[84px]"
        options={PAGE_SIZE_OPTIONS}
        value={pageSize}
        onChange={(value) => onPageSizeChange(Number(value))}
        data-testid="rules-dbmmatrixtable-page-size"
      />
    </div>
    <Pagination
      page={page}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={onPageChange}
      showSummary={false}
      size="sm"
      data-testid="rules-dbmmatrixtable-pagination"
    />
  </div>
);

export default DBMMatrixTable;
